import type { FormTreeNode } from '../components/utils/formTreeUtils';
import type { StudyForm } from '../components/types';
import {
  generateFormTemplateCsv,
  generateFormDemoCsv,
} from './csvTemplateGenerator';
import type { DemoDataSettings } from '../types/demoDataSettings';

/**
 * Flatten tree to get all forms recursively
 */
function flattenTree(nodes: FormTreeNode[]): FormTreeNode[] {
  const result: FormTreeNode[] = [];

  function dfs(node: FormTreeNode) {
    result.push(node);
    node.children.forEach(child => dfs(child));
  }

  nodes.forEach(node => dfs(node));
  return result;
}

/**
 * Find all parent forms for a given form
 * A form can be a subform of multiple parents (e.g., nested_lvl3a can be child of nested_lvl2a and nested_lvl2b)
 */
function findAllParentForms(form: StudyForm, allForms: StudyForm[]): StudyForm[] {
  const parents: StudyForm[] = [];

  for (const potentialParent of allForms) {
    if (
      potentialParent.json_schema?.subFormArray &&
      Array.isArray(potentialParent.json_schema.subFormArray)
    ) {
      if (potentialParent.json_schema.subFormArray.includes(form.name)) {
        parents.push(potentialParent);
      }
    }
  }

  return parents;
}

/**
 * Generate filename for form CSV with optional parent suffix
 */
function generateFilename(formName: string, type: 'template' | 'demo', parentName?: string): string {
  const baseName = parentName ? `${formName} (${parentName})` : formName;
  const suffix = type === 'template' ? '_template.csv' : '.csv';
  return `${baseName}${suffix}`;
}

/**
 * Generate archive name with demo data settings
 * Format: study_forms_demo_sites[1]_rows25_start1_template[10-$&-$nnn].zip
 */
function generateArchiveName(demoDataSettings?: DemoDataSettings): string {
  if (!demoDataSettings) {
    return 'study_forms_demos.zip';
  }

  const { clinicalSiteIds, numberOfRows, startSubjectNumber, subjectIdTemplate } = demoDataSettings;
  
  const sitesPart = `sites[${clinicalSiteIds.join(',')}]`;
  const rowsPart = `rows${numberOfRows}`;
  const startPart = `start${startSubjectNumber}`;
  const templatePart = `template[${subjectIdTemplate}]`;
  
  return `study_forms_demo_${sitesPart}_${rowsPart}_${startPart}_${templatePart}.zip`;
}

/**
 * Create a ZIP file from multiple CSV files
 * Note: This requires JSZip library. Install with: npm install jszip
 * If JSZip is not available, will throw error and fallback to individual downloads
 * @param forms - Tree of forms to generate CSV for
 * @param type - Type of CSV to generate ('template' or 'demo')
 * @param allForms - Optional flat array of all forms for parent lookup (if not provided, will use forms from tree)
 * @param demoDataSettings - Optional demo data settings (only used when type is 'demo')
 */
export async function createCsvArchive(
  forms: FormTreeNode[],
  type: 'template' | 'demo',
  allForms?: StudyForm[],
  demoDataSettings?: DemoDataSettings
): Promise<Blob> {
  // Dynamic import of JSZip (lazy load)
  let JSZip;
  try {
    JSZip = (await import('jszip')).default;
  } catch {
    throw new Error('JSZip library is not installed. Please install it with: npm install jszip');
  }

  const zip = new JSZip();

  const treeForms = flattenTree(forms);

  // Create a flat array of all forms for parent lookup
  // Use provided allForms if available, otherwise use forms from tree
  const allFormsFlat: StudyForm[] = allForms || (treeForms as StudyForm[]);

  // Track generated filenames to avoid duplicates
  const generatedFiles = new Set<string>();

  treeForms.forEach(form => {
    // Find all parent forms for this form
    const parentForms = findAllParentForms(form, allFormsFlat);

    if (parentForms.length > 0) {
      // Form has multiple parents - generate CSV for each parent
      parentForms.forEach(parentForm => {
        const csv = type === 'template'
          ? generateFormTemplateCsv(form, parentForm.id)
          : generateFormDemoCsv(form, parentForm.id, demoDataSettings);

        const filename = generateFilename(form.name, type, parentForm.name);

        // Only add if not already generated (avoid duplicates)
        if (!generatedFiles.has(filename)) {
          zip.file(filename, csv);
          generatedFiles.add(filename);
        }
      });
    } else {
      // Form has no parents or single parent (from tree) - generate single CSV
      const csv = type === 'template'
        ? generateFormTemplateCsv(form)
        : generateFormDemoCsv(form, undefined, demoDataSettings);

      const filename = generateFilename(form.name, type);

      if (!generatedFiles.has(filename)) {
        zip.file(filename, csv);
        generatedFiles.add(filename);
      }
    }
  });

  return await zip.generateAsync({ type: 'blob' });
}

/**
 * Download ZIP archive
 * @param forms - Tree of forms to generate CSV for
 * @param type - Type of CSV to generate ('template' or 'demo')
 * @param allForms - Optional flat array of all forms for parent lookup
 * @param demoDataSettings - Optional demo data settings (only used when type is 'demo')
 */
export async function downloadCsvArchive(
  forms: FormTreeNode[],
  type: 'template' | 'demo',
  allForms?: StudyForm[],
  demoDataSettings?: DemoDataSettings
): Promise<void> {
  try {
    const blob = await createCsvArchive(forms, type, allForms, demoDataSettings);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const archiveName = type === 'template'
      ? 'study_forms_templates.zip'
      : generateArchiveName(demoDataSettings);

    link.href = url;
    link.download = archiveName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    console.error('Error creating archive:', error);
    // Fallback: download files individually
    const treeForms = flattenTree(forms);

    // Create a flat array of all forms for parent lookup
    // Use provided allForms if available, otherwise use forms from tree
    const allFormsFlat: StudyForm[] = allForms || (treeForms as StudyForm[]);

    let fileIndex = 0;

    treeForms.forEach(form => {
      // Find all parent forms for this form
      const parentForms = findAllParentForms(form, allFormsFlat);

      if (parentForms.length > 0) {
        // Form has multiple parents - generate CSV for each parent
        parentForms.forEach(parentForm => {
          setTimeout(() => {
            const csv = type === 'template'
              ? generateFormTemplateCsv(form, parentForm.id)
              : generateFormDemoCsv(form, parentForm.id, demoDataSettings);

            const filename = generateFilename(form.name, type, parentForm.name);

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);
          }, 100 * fileIndex++);
        });
      } else {
        // Form has no parents or single parent - generate single CSV
        setTimeout(() => {
          const csv = type === 'template'
            ? generateFormTemplateCsv(form)
            : generateFormDemoCsv(form, undefined, demoDataSettings);

          const filename = generateFilename(form.name, type);

          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 100);
        }, 100 * fileIndex++);
      }
    });
  }
}

